import { useMemo, useState } from "react";
import {
    ProductBeverageItem,
    TabItem,
} from "@hatch-digital-nl/bravilor-storybook";
import { DstProductDetailViewModel } from "../interfaces/ProductDetailViewTypes";
import {
    buildDstSpecificationEntries,
    ProductSpecificationEntry,
} from "../utils/dstProductDetailAdapters";
import { mapDstBeveragesForDisplay } from "../utils/mapDstBeveragesForDisplay";
import { DstProductDetailLabels } from "../utils/productDetailLabels";
import {
    PRODUCT_SPECIFICATION_TAB,
    ProductSpecificationTabId,
} from "../utils/productSpecificationsConstants";

interface UseProductSpecificationsParams {
    data?: DstProductDetailViewModel;
    labels: DstProductDetailLabels;
    loading: boolean;
}

interface UseProductSpecificationsReturn {
    showSkeleton: boolean;
    showTabs: boolean;
    hasContent: boolean;
    tabs: TabItem[];
    cardTitle: string;
    specificationsLabel: string;
    activeTabId: ProductSpecificationTabId;
    setActiveTabId: React.Dispatch<
        React.SetStateAction<ProductSpecificationTabId>
    >;
    specificationEntries: ProductSpecificationEntry[];
    beveragesForDisplay: ProductBeverageItem[];
    isBeveragesTabActive: boolean;
}

export const useProductSpecifications = ({
    data,
    labels,
    loading,
}: UseProductSpecificationsParams): UseProductSpecificationsReturn => {
    const [activeTabId, setActiveTabId] = useState<ProductSpecificationTabId>(
        PRODUCT_SPECIFICATION_TAB.SPECIFICATIONS,
    );

    const beverageCount = data?.beverages?.length ?? 0;
    const hasDimensions = !!data?.dimensions;
    const hasSpecs =
        hasDimensions || (data?.productAttributes?.length ?? 0) > 0;
    const hasBeverages = beverageCount > 0;
    const hasContent = hasSpecs || hasBeverages;
    const showTabs = hasBeverages;
    const showSkeleton = loading;
    const specificationsLabel = labels.specifications;
    const beveragesLabel = labels.beverages;

    const tabs: TabItem[] = useMemo(
        () => [
            {
                id: PRODUCT_SPECIFICATION_TAB.SPECIFICATIONS,
                label: specificationsLabel,
            },
            {
                id: PRODUCT_SPECIFICATION_TAB.BEVERAGES,
                label: beveragesLabel,
                count: beverageCount,
            },
        ],
        [specificationsLabel, beveragesLabel, beverageCount],
    );

    const cardTitle =
        activeTabId === PRODUCT_SPECIFICATION_TAB.BEVERAGES
            ? beveragesLabel
            : specificationsLabel;

    const specificationEntries = useMemo(
        () =>
            data ? buildDstSpecificationEntries(data, labels.dimensions) : [],
        [data, labels.dimensions],
    );

    const beveragesForDisplay = useMemo(
        () => mapDstBeveragesForDisplay(data?.beverages),
        [data?.beverages],
    );

    const isBeveragesTabActive =
        showTabs && activeTabId === PRODUCT_SPECIFICATION_TAB.BEVERAGES;

    return {
        showSkeleton,
        showTabs,
        hasContent,
        tabs,
        cardTitle,
        specificationsLabel,
        activeTabId,
        setActiveTabId,
        specificationEntries,
        beveragesForDisplay,
        isBeveragesTabActive,
    };
};
